import {
  ScheduleMember,
  DefaultCrewMember,
  DefaultEquipmentMember,
  ScheduleEquipment,
  CurrentSchedule,
  RelatedMultischedules,
} from '@services/schedules/internal/types';
import { getFullName } from '@utils/textHelpers';
import { SchedulerSidebarEquipmentMember, SchedulerSidebarCrewMember } from './types';
import { Dropdown } from '@contexts/dropdowns/types';
import { UsersDropdown } from '@services/dropdowns/types';

export const getCrewMembersFromForeman = (
  foremanId: string,
  fieldUsers: Dropdown<UsersDropdown[]>,
) => {
  if (!foremanId || !fieldUsers.options.length) return [];

  return fieldUsers.options.find((el) => el.id === foremanId)?.crewMembers || [];
};

export const getEquipmentMembersFromForeman = (
  foremanId: string,
  fieldUsers: Dropdown<UsersDropdown[]>,
) => {
  if (!foremanId || !fieldUsers.options.length) return [];

  return fieldUsers.options.find((el) => el.id === foremanId)?.assetMembers || [];
};

export const mapCrewMembersToSchedulerSidebarCrewMembers = (
  crewMembers: DefaultCrewMember[],
): SchedulerSidebarCrewMember[] => {
  return crewMembers
    .map((el) => ({
      userId: el.id,
      name: getFullName(el.firstName, el.lastName),
      roles: el.roles || [],
      conflicts: el.conflicts || [],
      userDeactivated: !!el.discardedAt,
    }))
    .filter((el) => !el.userDeactivated);
};

export const mapResponseScheduleMembersToForm = (
  scheduleMembers: ScheduleMember[],
): SchedulerSidebarCrewMember[] => {
  return scheduleMembers
    .map((el) => ({
      id: el.id,
      userId: el.user.id,
      name: getFullName(el.user.firstName, el.user.lastName),
      roles: el.user.roles,
      conflicts: el.conflicts || [],
      discardedAt: el.discardedAt,
      userDeactivated: !!el.user.discardedAt,
    }))
    .filter((el) => !el.discardedAt);
};

export const mapEquipmentMembersToSchedulerSidebarCrewMembers = (
  equipmentMembers: DefaultEquipmentMember[],
): SchedulerSidebarEquipmentMember[] => {
  return equipmentMembers
    .map((el) => ({
      assetId: el.id,
      name: el.name,
      internalId: el.internalId,
      conflicts: el.conflicts || [],
      equipmentDeactivated: !!el.discardedAt,
    }))
    .filter((el) => !el.equipmentDeactivated);
};

export const mapResponseScheduleEquipmentToForm = (
  scheduleEquipment: ScheduleEquipment[],
): SchedulerSidebarEquipmentMember[] => {
  return scheduleEquipment
    .map((el) => ({
      id: el.id,
      assetId: el.asset.id,
      name: el.asset.name,
      internalId: el.asset.internalId,
      conflicts: el.conflicts || [],
      discardedAt: el.discardedAt,
      equipmentDeactivated: !!el.asset.discardedAt,
    }))
    .filter((el) => !el.discardedAt);
};

export const excludeFieldUserFromCrew = (
  crewMembers: SchedulerSidebarCrewMember[],
  fieldUserId: string,
) => {
  return crewMembers.filter((el) => el.userId !== fieldUserId);
};

const sameIds = (first: string[], second: string[]) => {
  const ids = new Set(first);
  return ids.size === new Set(second).size && second.every((id) => ids.has(id));
};

const sameTime = (first?: string | null, second?: string | null) =>
  (first ? new Date(first).getTime() : null) === (second ? new Date(second).getTime() : null);

export const getHasScheduleChanges = ({
  schedule,
  currentSchedule,
  crewMembers,
  equipmentMembers,
}: {
  schedule?: { foreman?: { id?: string } | null; startsAt?: string | null; endsAt?: string | null };
  currentSchedule?: CurrentSchedule;
  crewMembers: SchedulerSidebarCrewMember[];
  equipmentMembers: SchedulerSidebarEquipmentMember[];
}) => {
  if (!currentSchedule?.id || !schedule) return false;

  const foremanId = currentSchedule.foreman?.id || '';
  const initialCrew = excludeFieldUserFromCrew(
    mapResponseScheduleMembersToForm(currentSchedule.scheduleMembers || []),
    foremanId,
  );
  const initialEquipment = mapResponseScheduleEquipmentToForm(currentSchedule.scheduleAssets || []);

  return (
    (schedule.foreman?.id || '') !== foremanId ||
    !sameTime(schedule.startsAt, currentSchedule.startsAt) ||
    !sameTime(schedule.endsAt, currentSchedule.endsAt) ||
    !sameIds(
      crewMembers.map((el) => el.userId),
      initialCrew.map((el) => el.userId),
    ) ||
    !sameIds(
      equipmentMembers.map((el) => el.assetId),
      initialEquipment.map((el) => el.assetId),
    )
  );
};

const sortedRelatedDays = (relatedMultischedules: RelatedMultischedules) =>
  Object.values(relatedMultischedules || {})
    .flat()
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

export const isExistingMultiDay = (relatedMultischedules: RelatedMultischedules) =>
  sortedRelatedDays(relatedMultischedules).length > 1;

export const getMultiDayPosition = ({
  relatedMultischedules,
  currentSchedule,
  plannedDays = 1,
}: {
  relatedMultischedules?: RelatedMultischedules;
  currentSchedule?: CurrentSchedule;
  plannedDays?: number;
}) => {
  const days = sortedRelatedDays(relatedMultischedules);
  const index = days.findIndex((el) => el.id === currentSchedule?.id);
  if (days.length > 1 && index >= 0) return { day: index + 1, total: days.length };
  if (plannedDays > 1) return { day: 1, total: plannedDays };
  return null;
};
